import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, EmptyState, ConfirmDialog } from '@/components/ui';
import { useBoqVersions } from './hooks/useBoqVersions';
import { useConstructionBoqDetail } from './hooks/useConstructionBoqDetail';
import { toast } from 'sonner';

// Components
import { BoqVersionDesktopTable } from './components/BoqVersionDesktopTable';
import { BoqVersionTimeline } from './components/BoqVersionTimeline';

export default function BoqVersionsPage() {
  const { id: boqId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { boq, isLoading: isBoqLoading } = useConstructionBoqDetail(boqId || '');
  const { versions, isLoading, createVersion, isCreating } = useBoqVersions(boqId || '');

  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('timeline');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleCreateVersion = async () => {
    try {
      await createVersion({
        version_number: versions.length + 1,
        version_name: `Phiên bản v${versions.length + 1} - ${new Date().toLocaleDateString('vi-VN')}`,
        description: 'Tự động khởi tạo phiên bản mới từ dữ liệu hiện tại.',
        status_code: 'draft',
        total_amount: boq?.total_amount || 0,
        company_id: boq?.company_id,
        project_id: boq?.project_id
      });
      setShowCreateModal(false);
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleViewDetail = (v: any) => {
    toast.info('Tính năng xem chi tiết phiên bản lịch sử đang được phát triển.');
  };

  const handleRestore = (v: any) => {
    toast.info(`Đang chuẩn bị khôi phục về phiên bản v${v.version_number}...`);
  };

  return (
    <PageLayout
      title="Lịch sử Phiên bản BOQ"
      description={boq ? `Quản lý và theo dõi các thay đổi của: ${boq.boq_name}` : 'Lịch sử thay đổi và các phiên bản đã lưu của bảng khối lượng.'}
      breadcrumbs={[
        { label: 'Thi công' }, 
        { label: 'BOQ', path: '/construction/boq' }, 
        { label: boq?.boq_name || 'Chi tiết', path: `/construction/boq/detail/${boqId}` },
        { label: 'Phiên bản' }
      ]}
      isLoading={isBoqLoading}
      headerActions={
        <div className="flex gap-2">
          <div className="flex bg-slate-100 p-1 rounded-lg mr-2">
            <button 
              className={`p-1.5 rounded-md transition-all ${viewMode === 'timeline' ? 'bg-white shadow-sm text-os-primary' : 'text-slate-400 hover:text-slate-600'}`}
              onClick={() => setViewMode('timeline')}
              title="Xem dạng Timeline"
            >
              <i className="ri-history-line" />
            </button>
            <button 
              className={`p-1.5 rounded-md transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-os-primary' : 'text-slate-400 hover:text-slate-600'}`}
              onClick={() => setViewMode('table')}
              title="Xem dạng Bảng"
            >
              <i className="ri-table-line" />
            </button>
          </div>
          <Button 
            variant="primary" 
            icon="ri-add-line" 
            onClick={() => setShowCreateModal(true)}
            isLoading={isCreating}
          >
            Chốt Phiên bản mới
          </Button>
        </div>
      }
    >
      <div className="max-w-6xl mx-auto py-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-4">
            <div className="w-12 h-12 border-4 border-os-primary/20 border-t-os-primary rounded-full animate-spin" />
            <p className="text-slate-500 font-medium">Đang tải lịch sử phiên bản...</p>
          </div>
        ) : versions.length === 0 ? (
          <EmptyState
            icon="ri-history-line"
            title="Chưa có phiên bản nào"
            description="Hãy chốt phiên bản đầu tiên để lưu lại trạng thái hiện tại của BOQ và theo dõi các thay đổi sau này."
            action={
              <Button onClick={() => setShowCreateModal(true)}>Chốt phiên bản đầu tiên</Button>
            }
          />
        ) : viewMode === 'timeline' ? (
          <div className="px-4">
            <BoqVersionTimeline versions={versions} />
          </div>
        ) : (
          <BoqVersionDesktopTable 
            versions={versions} 
            onViewDetail={handleViewDetail}
            onRestore={handleRestore}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={showCreateModal}
        title="Xác nhận Chốt Phiên bản"
        message="Hệ thống sẽ lưu lại toàn bộ hạng mục và giá trị hiện tại của BOQ thành một phiên bản cố định. Bạn có muốn tiếp tục?"
        confirmText="Chốt Phiên bản"
        onConfirm={handleCreateVersion}
        onClose={() => setShowCreateModal(false)}
        isLoading={isCreating}
      />
    </PageLayout>
  );
}
